import React, { useMemo, useCallback } from 'react';
import { styled } from '@compiled/react';
import { Field } from '@atlaskit/form';
import Select from '@atlaskit/select';
import { token } from '@atlaskit/tokens';
import { useIntl } from '@atlassian/jira-intl';
import {
	useAnalyticsEvents as useAnalyticsEventsDI,
	fireUIAnalytics as fireUIAnalyticsDI,
} from '@atlassian/jira-product-analytics-bridge';
import type { RouterProps, DateQuery } from '../../common/helpers/types.tsx';
import { LabelWrapper } from '../../common/ui/label/index.tsx';
import { useUIStateStore } from '../../controllers/ui-state-store/index.tsx';
import { TIMESCALE_OPTIONS, CUSTOM_TIMESCALE_ID } from './constants.tsx';
import DatePickerGroup from './date-picker-group/main.tsx';
import type { DateTimeBreakdown } from './date-picker-group/time-breakdown/types.tsx';
import messages from './messages.tsx';
import type { Item, DropdownOption, LastSearchQuery } from './types.tsx';
import { getSelectedOptionByRouterProps, mapToDateQuery } from './utils.tsx';

type Props = {
	loading: boolean;
	routerProps: RouterProps;
	lastSearchQuery: LastSearchQuery;
	fireUIAnalytics?: typeof fireUIAnalyticsDI;
	useAnalyticsEvents?: typeof useAnalyticsEventsDI;
	onSelect: (arg1: DateQuery) => void;
};

const itemClickedEvent = {
	eventType: 'UI',
	eventName: 'item clicked',
	action: 'clicked',
	actionSubject: 'dropdownItem',
} as const;

const DateFilter = ({
	loading,
	routerProps,
	lastSearchQuery,
	onSelect,
	fireUIAnalytics = fireUIAnalyticsDI,
	useAnalyticsEvents = useAnalyticsEventsDI,
}: Props) => {
	const [{ dateFilterQuery: savedDateFilterQuery }] = useUIStateStore();
	const intl = useIntl();

	const savedTimescaleId = useMemo(
		() => (savedDateFilterQuery ? savedDateFilterQuery.timescaleId : undefined),
		[savedDateFilterQuery],
	);

	const { createAnalyticsEvent } = useAnalyticsEvents();

	const onDropdownSelect = useCallback(
		({ id }: DropdownOption) => {
			onSelect(
				mapToDateQuery({ reportId: routerProps.reportId, timescaleId: id }, lastSearchQuery),
			);
			const timescaleOption = TIMESCALE_OPTIONS.find((option) => option.id === id);
			fireUIAnalytics(
				createAnalyticsEvent({
					actionSubject: 'dropdownItem',
					action: 'clicked',
				}),
				'dateFilter',
				{
					periodViewed: timescaleOption ? timescaleOption.analyticsKey : 'unknown',
				},
			);
		},
		[fireUIAnalytics, lastSearchQuery, onSelect, routerProps.reportId, createAnalyticsEvent],
	);

	const handleChange = useCallback(
		(option: DropdownOption | null) => {
			if (option) {
				onDropdownSelect(option);
			}
		},
		[onDropdownSelect],
	);

	const onCustomDateRangeUpdate = useCallback(
		(args: DateTimeBreakdown) => {
			const analyticsEvent = createAnalyticsEvent(itemClickedEvent);
			const dateQuery = mapToDateQuery({ ...routerProps, ...args }, lastSearchQuery);
			onSelect(dateQuery);
			const timescaleOption = TIMESCALE_OPTIONS.find((option) => option.id === CUSTOM_TIMESCALE_ID);
			fireUIAnalytics(analyticsEvent, 'dateFilter', {
				periodViewed: timescaleOption ? timescaleOption.analyticsKey : 'unknown',
			});
		},
		[createAnalyticsEvent, fireUIAnalytics, lastSearchQuery, onSelect, routerProps],
	);

	const dropdownOptions = useMemo(
		(): DropdownOption[] =>
			TIMESCALE_OPTIONS.map(({ id, message }: Item) => ({
				id,
				value: `${id}`,
				label: intl.formatMessage(message),
			})),
		[intl],
	);

	const selectedOption = useMemo(
		(): DropdownOption =>
			getSelectedOptionByRouterProps(dropdownOptions, routerProps, savedTimescaleId),
		[savedTimescaleId, dropdownOptions, routerProps],
	);

	const showLabel = useMemo(() => intl.formatMessage(messages.dropdownLabel), [intl]);

	const dateTimeBreakdown = useMemo(() => {
		let source = routerProps;
		// If it's a transition use the saved value
		// @ts-expect-error - TS2367 - This condition will always return 'true' since the types 'RouterProps' and 'number' have no overlap.
		if (routerProps !== CUSTOM_TIMESCALE_ID && savedDateFilterQuery) {
			source = savedDateFilterQuery;
		}
		return {
			startDate: source.startDate,
			endDate: source.endDate,
			timeBreakdown: source.timeBreakdown,
		};
	}, [savedDateFilterQuery, routerProps]);

	return (
		<DateFilterContainer>
			<div data-test-id="servicedesk-reports-common.ui.date-filter.timescale-picker">
				<LabelWrapper>
					<Field name="showLabel" label={showLabel} id="date-filter-dropdown">
						{() => (
							<div>
								<Select
									inputId="date-filter-dropdown"
									aria-labelledby="date-filter-dropdown-label"
									value={selectedOption}
									options={dropdownOptions}
									spacing="compact"
									hideSelectedOptions
									onChange={handleChange}
									controlShouldRenderValue
									styles={{
										menu: (base) => ({
											...base,
											minWidth: '160px',
										}),
									}}
								/>
							</div>
						)}
					</Field>
				</LabelWrapper>
			</div>
			{selectedOption.id === CUSTOM_TIMESCALE_ID && (
				<DatePickerGroup
					loading={loading}
					onUpdate={onCustomDateRangeUpdate}
					dateTimeBreakdown={dateTimeBreakdown}
				/>
			)}
		</DateFilterContainer>
	);
};

export default DateFilter;

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const DateFilterContainer = styled.div({
	display: 'flex',
	margin: `0 ${token('space.500', '40px')} ${token('space.300', '24px')} 0`,
});
